import { UnsupportedUploadTypeError } from '@client/components/ajax';
import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityFeedPostFragmentDoc,
  MediaInput,
  useCommunitiesV2AddPostMutation,
} from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';
import { feedVariantOf, variantForStatus } from '../lib/feedCacheVariants';
import { uploadMediaInputs } from '../lib/mediaInputs';

type AddPostInput = {
  text: string;
  files: File[];
  videoUuids: string[];
  publishAt?: Date | null;
};

export type AddPostResult =
  | { type: 'success' }
  | { type: 'failure'; reason: string };

const toFailureReason = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

export const useAddPost = (communityId: CommunityId, influencerId?: string) => {
  const [addPostMutation] = useCommunitiesV2AddPostMutation();
  const creatorId = resolveInfluencerId(influencerId);

  return useCallback(
    async ({
      text,
      files,
      videoUuids,
      publishAt,
    }: AddPostInput): Promise<AddPostResult> => {
      try {
        let mediaInput: MediaInput[] = [];
        if (files.length > 0 || videoUuids.length > 0) {
          if (creatorId === null) throw new Error('Missing creator id');
          mediaInput = await uploadMediaInputs(files, videoUuids, creatorId);
        }

        await addPostMutation({
          variables: {
            communityId,
            text,
            media: mediaInput,
            ...(publishAt !== undefined && {
              publishAt: publishAt && publishAt.toISOString(),
            }),
          },
          update: (cache, { data }) => {
            const created = data?.addPost;
            if (!created) return;

            const ref = cache.writeFragment({
              fragment: CommunityFeedPostFragmentDoc,
              fragmentName: 'CommunityFeedPost',
              data: created,
            });
            if (!ref) return;

            cache.modify<{ posts: Reference[] }>({
              id: cache.identify({ __typename: 'Community', id: communityId }),
              fields: {
                posts: (
                  existing: readonly Reference[] = [],
                  { storeFieldName, readField },
                ) => {
                  const belongsHere =
                    feedVariantOf(storeFieldName) ===
                    variantForStatus(created.status);
                  if (!belongsHere) return existing;
                  const present = existing.some(
                    (postRef) => readField('id', postRef) === created.id,
                  );
                  return present ? existing : [ref, ...existing];
                },
              },
            });
          },
        });
        return { type: 'success' };
      } catch (error) {
        toast.error(
          error instanceof UnsupportedUploadTypeError
            ? error.message
            : 'Something went wrong',
        );
        return { type: 'failure', reason: toFailureReason(error) };
      }
    },
    [addPostMutation, communityId, creatorId],
  );
};
